import { useCallback, useEffect, useState } from "react";
import { Download, X, ShieldCheck, Info } from "lucide-react";
import { usePwaInstall } from "@/hooks/usePwaInstall";

const DISMISSED_KEY = "sw_install_dismissed_until";
const DISMISS_DAYS = 1; // reduced from 7 → re-show next day

function isDismissed() {
  if (typeof window === "undefined") return false;
  try {
    const until = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
    return until > Date.now();
  } catch (e) {
    return false;
  }
}

/**
 * Bottom auto-banner shown when the app is installable. The persistent
 * "Install App" header button is rendered separately via HeaderInstallButton.
 * Both share the same `usePwaInstall` hook so the install event isn't lost.
 */
export default function InstallPWAButton() {
  const { canInstall, isIos, isStandalone, install } = usePwaInstall();
  const [bannerOpen, setBannerOpen] = useState(true);
  const [showIos, setShowIos] = useState(false);

  // iOS: show hint after a short delay (Safari doesn't fire beforeinstallprompt)
  useEffect(() => {
    if (isStandalone || isDismissed()) return undefined;
    if (!isIos) return undefined;
    const t = setTimeout(() => setShowIos(true), 4000);
    return () => clearTimeout(t);
  }, [isIos, isStandalone]);

  // Reset open state when canInstall becomes true again (after dismiss expiry)
  useEffect(() => { setBannerOpen(true); }, [canInstall]);

  const dismiss = useCallback(() => {
    try {
      const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(DISMISSED_KEY, String(until));
    } catch (e) { /* ignore */ }
    setBannerOpen(false);
    setShowIos(false);
  }, []);

  const handleInstall = useCallback(async () => {
    await install();
    setBannerOpen(false);
  }, [install]);

  const visible = (canInstall || showIos) && bannerOpen && !isStandalone && !isDismissed();
  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[92%] max-w-md px-3" data-testid="pwa-install-banner">
      <div className="relative rounded-2xl shadow-2xl border border-white/10 bg-gradient-to-br from-amethyst-900 via-amethyst-800 to-magenta-700 text-white p-4 sm:p-5">
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-white/10 text-white/70 hover:text-white"
          data-testid="pwa-install-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
            <Download className="w-5 h-5 text-gold-300" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-base sm:text-lg font-semibold leading-tight">Install Shakti Wings</p>
            {showIos && !canInstall ? (
              <p className="text-xs sm:text-sm text-amethyst-100 mt-1 leading-relaxed">
                Tap <span className="inline-block px-1.5 py-0.5 rounded bg-white/10 font-mono text-[10px]">Share ↑</span> then <span className="font-semibold">Add to Home Screen</span> for app-like access.
              </p>
            ) : (
              <p className="text-xs sm:text-sm text-amethyst-100 mt-1">
                Add to home screen for fast, app-like access — no Play Store needed.
              </p>
            )}
          </div>
        </div>
        {canInstall && (
          <>
            <button
              onClick={handleInstall}
              className="mt-3 w-full rounded-full bg-gold-400 hover:bg-gold-500 text-amethyst-900 font-semibold py-2.5 text-sm transition-transform active:scale-[0.98]"
              data-testid="pwa-install-cta"
            >
              Install App
            </button>
            <div className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-white/8 border border-white/10" data-testid="pwa-install-reassurance">
              <Info className="w-3.5 h-3.5 text-gold-300 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
              <p className="text-[11px] sm:text-xs text-amethyst-100/90 leading-relaxed">
                Some phones may show <span className="font-semibold text-white">&ldquo;Use with caution&rdquo;</span> — this is normal for new apps. Tap <span className="font-semibold text-white">Install Anyway</span>. <ShieldCheck className="inline w-3 h-3 -mt-0.5 text-gold-300" strokeWidth={2.4} /> Your data is fully safe with us.
              </p>
            </div>
          </>
        )}
        {showIos && !canInstall && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-white/8 border border-white/10">
            <ShieldCheck className="w-3.5 h-3.5 text-gold-300 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
            <p className="text-[11px] sm:text-xs text-amethyst-100/90 leading-relaxed">
              100% safe — opens just like an app from your home screen.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
